/**
 * The tenant settings an extension run may read, types only: the keys (`ExtensionSettingKey`) and what each key's definition
 * declares (`ExtensionSettingDefinition`). The definitions themselves, with their projections, are `EXTENSION_SETTINGS`.
 *
 * The list is a product decision and intentionally short. A run reads a setting through
 * `GET /api/extensions/runs/:runId/settings/:key`, which serves only the key's projection, never the stored document; adding a
 * key exposes its projection to every extension the tenant runs, so a key is added only when a run needs it and its projection
 * holds nothing secret.
 */
import type { JsonSafeMetadata } from '../surface.engine/json.safe.metadata.zod';
import type { GeneralSetting } from '../settings/general.settings';

/** The settings a run may read; the value is the key in the route, in `{{settings.<key>}}` and in a workflow's setting value. */
export enum ExtensionSettingKey {
    /** The tenant's IANA time zone (`TenantSettingKey.GeneralSetting` → `region.timezone`), such as `Australia/Sydney`. */
    TimeZone = 'timeZone',
    /** The tenant's working day (`TenantSettingKey.GeneralSetting` → `schedule`): start and end times and the working days. */
    BusinessHours = 'businessHours',
}

/** One field of an object setting, readable as `<key>.<path>`. */
export interface ExtensionSettingField {
    path: string;
    metadata: JsonSafeMetadata;
}

/** What one `ExtensionSettingKey` serves and how the workflow editor types it. */
export interface ExtensionSettingDefinition {
    /** The binding picker's label. */
    title: string;
    /** The type of the whole value. */
    metadata: JsonSafeMetadata;
    /** The fields of an object value, offered one by one in the binding picker; empty for a scalar. */
    fields: ExtensionSettingField[];
    /** The public value from the tenant's general settings; returns only the fields declared here, never the source object. */
    project(general: Partial<GeneralSetting>): unknown;
}
